// Company Process Service - handles company-specific process operations

import {
  ProcessTask,
  ProcessTaskWithArtifacts,
  ProcessValidation,
  ProcessTransition,
  ValidationError,
  ValidationWarning,
  ValidationSuggestion,
} from '../types/processTask';
import { ArtifactProcessService } from './api/artifactProcessService';

export class CompanyProcessService {
  // Get all company processes for an artifact
  async getProcessesForArtifact(artifactId: string): Promise<ProcessTask[]> {
    try {
      console.log('Getting company processes for artifact:', artifactId);

      // Call the actual API endpoint for company processes
      const artifactProcesses = await ArtifactProcessService.getByArtifactId(artifactId);

      // Map ArtifactProcess to ProcessTask
      return artifactProcesses.map((process) => ({
        id: process.id,
        name: process.name,
        description: process.description,
        process_type: process.process_type,
        demand_flow_type: process.demand_flow_type,
        demand_type: process.demand_type,
        is_active: process.is_active,
        created_at: process.created_at,
        updated_at: process.updated_at || process.created_at,
        artifact_ref: process.artifact_ref,
        process_parameters: process.process_parameters,
        validation_rules: process.validation_rules,
        demand_calculation_rules: process.demand_calculation_rules,
        improvement_ref: process.improvement_ref,
        improvement_parameters: process.improvement_parameters,
      }));
    } catch (error) {
      console.error('Error getting company processes for artifact:', error);
      throw error;
    }
  }

  // Get all company processes
  async getAllProcesses(): Promise<ProcessTask[]> {
    try {
      console.log('Getting all company processes');

      // Call the actual API endpoint for all company processes
      const artifactProcesses = await ArtifactProcessService.getAll();

      // Map ArtifactProcess to ProcessTask
      return artifactProcesses.map((process) => ({
        id: process.id,
        name: process.name,
        description: process.description,
        process_type: process.process_type,
        demand_flow_type: process.demand_flow_type,
        demand_type: process.demand_type,
        is_active: process.is_active,
        created_at: process.created_at,
        updated_at: process.updated_at || process.created_at,
        artifact_ref: process.artifact_ref,
        process_parameters: process.process_parameters,
        validation_rules: process.validation_rules,
        demand_calculation_rules: process.demand_calculation_rules,
        improvement_ref: process.improvement_ref,
        improvement_parameters: process.improvement_parameters,
      }));
    } catch (error) {
      console.error('Error getting all company processes:', error);
      throw error;
    }
  }

  // Get company process with all artifacts and transitions
  async getProcessWithArtifacts(processId: string): Promise<ProcessTaskWithArtifacts> {
    try {
      // TODO: Implement company process API endpoint
      // For now, return a mock structure
      console.log('Getting company process with artifacts:', processId);

      // Mock response structure
      const mockProcess: ProcessTaskWithArtifacts = {
        id: processId,
        name: 'Mock Company Process',
        description: 'This is a mock company process',
        process_type: 'DEMAND',
        demand_flow_type: 'DRUM',
        demand_type: 'TARGET',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        artifact_ref: 'mock-artifact-id',
        process_parameters: {},
        validation_rules: {},
        demand_calculation_rules: {},
        improvement_ref: undefined,
        improvement_parameters: {},
        artifacts: {
          processItems: [],
          materials: [],
          resources: [],
          creations: [],
          processItemsPersistent: [],
        },
        transitions: [],
        validation: {
          is_complete: false,
          errors: [
            {
              id: 'mock-error-1',
              type: 'MISSING_ARTIFACT',
              message: 'Company process implementation not yet complete',
              fixable: true,
              suggested_fix: 'Implement company process API endpoints',
            },
          ],
          warnings: [],
          suggestions: [
            {
              id: 'mock-suggestion-1',
              type: 'ADD_ARTIFACT',
              message: 'Add artifacts to this process',
              action: 'Use the artifact-centric entry point to add artifacts',
            },
          ],
        },
      };

      return mockProcess;
    } catch (error) {
      console.error('Error getting company process with artifacts:', error);
      throw error;
    }
  }

  // Add artifact to company process with role
  async addArtifactToProcess(processId: string, artifactId: string, role: string): Promise<void> {
    try {
      // TODO: Implement company process API endpoint
      console.log(
        `Adding artifact ${artifactId} to company process ${processId} with role ${role}`
      );
    } catch (error) {
      console.error('Error adding artifact to company process:', error);
      throw error;
    }
  }

  // Remove artifact from company process
  async removeArtifactFromProcess(processId: string, artifactId: string): Promise<void> {
    try {
      // TODO: Implement company process API endpoint
      console.log(`Removing artifact ${artifactId} from company process ${processId}`);
    } catch (error) {
      console.error('Error removing artifact from company process:', error);
      throw error;
    }
  }

  // Update artifact role in company process
  async updateArtifactRole(processId: string, artifactId: string, newRole: string): Promise<void> {
    try {
      // TODO: Implement company process API endpoint
      console.log(
        `Updating artifact ${artifactId} role to ${newRole} in company process ${processId}`
      );
    } catch (error) {
      console.error('Error updating artifact role in company process:', error);
      throw error;
    }
  }

  // Create new company process
  async createProcess(process: Omit<ProcessTask, 'id'>): Promise<ProcessTask> {
    try {
      // TODO: Implement company process API endpoint
      console.log('Creating company process:', process);

      // Mock response
      const mockProcess: ProcessTask = {
        ...process,
        id: `company-process-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      return mockProcess;
    } catch (error) {
      console.error('Error creating company process:', error);
      throw error;
    }
  }

  // Update company process
  async updateProcess(processId: string, process: Partial<ProcessTask>): Promise<ProcessTask> {
    try {
      // TODO: Implement company process API endpoint
      console.log('Updating company process:', processId, process);

      // Mock response
      const mockProcess: ProcessTask = {
        id: processId,
        name: 'Updated Company Process',
        description: 'This is an updated company process',
        process_type: 'DEMAND',
        demand_flow_type: 'DRUM',
        demand_type: 'TARGET',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        artifact_ref: 'mock-artifact-id',
        process_parameters: {},
        validation_rules: {},
        demand_calculation_rules: {},
        improvement_ref: undefined,
        improvement_parameters: {},
        ...process,
      };

      return mockProcess;
    } catch (error) {
      console.error('Error updating company process:', error);
      throw error;
    }
  }

  // Delete company process
  async deleteProcess(processId: string): Promise<void> {
    try {
      // TODO: Implement company process API endpoint
      console.log('Deleting company process:', processId);
    } catch (error) {
      console.error('Error deleting company process:', error);
      throw error;
    }
  }

  // Validate company process
  async validateProcess(
    process: ProcessTask,
    artifacts: ProcessTaskWithArtifacts['artifacts'],
    _transitions: ProcessTransition[]
  ): Promise<ProcessValidation> {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];
    const suggestions: ValidationSuggestion[] = [];

    // Check if process has at least one process item
    if (artifacts.processItems.length === 0) {
      errors.push({
        id: 'missing-process-item',
        type: 'MISSING_ARTIFACT',
        message: 'Company process must have at least one PROCESSITEM artifact',
        fixable: true,
        suggested_fix: 'Add an artifact with PROCESSITEM role',
      });
    }

    // Check if all artifacts have transitions
    const allArtifacts = [
      ...artifacts.processItems,
      ...artifacts.materials,
      ...artifacts.resources,
      ...artifacts.creations,
      ...artifacts.processItemsPersistent,
    ];

    for (const artifact of allArtifacts) {
      if (artifact.transitions.length === 0) {
        warnings.push({
          id: `no-transitions-${artifact.artifact_id}`,
          type: 'INCOMPLETE_PROCESS',
          message: `Artifact ${artifact.artifact_name} has no transitions`,
          artifact_id: artifact.artifact_id,
        });
      }
    }

    // Add implementation warning
    warnings.push({
      id: 'implementation-warning',
      type: 'INCOMPLETE_PROCESS',
      message: 'Company process management is not yet fully implemented',
    });

    return {
      is_complete: errors.length === 0,
      errors,
      warnings,
      suggestions,
    };
  }
}
